import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import {
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableHead,
    TableCell,
} from "@/components/ui/table";

// Custom theme using CSS variables from index.css
const customCodeTheme = {
    'code[class*="language-"]': {
        color: 'var(--foreground)',
        background: 'none',
        fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
        fontSize: '12px',
        textAlign: 'left',
        whiteSpace: 'pre',
        wordSpacing: 'normal',
        wordBreak: 'normal',
        wordWrap: 'normal',
        lineHeight: '1.5',
        tabSize: '4',
        hyphens: 'none',
    },
    'pre[class*="language-"]': {
        color: 'var(--foreground)',
        background: 'var(--muted)',
        fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
        fontSize: '12px',
        textAlign: 'left',
        whiteSpace: 'pre',
        wordSpacing: 'normal',
        wordBreak: 'normal',
        wordWrap: 'normal',
        lineHeight: '1.5',
        tabSize: '4',
        hyphens: 'none',
        padding: '0.75rem',
        margin: '0.5rem 0',
        overflow: 'auto',
        borderRadius: '0.5rem',
        border: '1px solid var(--border)',
    },
    // Comments
    'comment': { color: 'var(--muted-foreground)', fontStyle: 'italic' },
    'prolog': { color: 'var(--muted-foreground)' },
    'doctype': { color: 'var(--muted-foreground)' },
    'cdata': { color: 'var(--muted-foreground)' },
    // Punctuation
    'punctuation': { color: 'var(--foreground)', opacity: '0.7' },
    // Namespace
    'namespace': { opacity: '0.7' },
    // Properties, tags, booleans, numbers
    'property': { color: 'var(--chart-2)' },
    'tag': { color: 'var(--chart-2)' },
    'boolean': { color: 'var(--chart-5)' },
    'number': { color: 'var(--chart-5)' },
    'constant': { color: 'var(--chart-5)' },
    'symbol': { color: 'var(--chart-5)' },
    'deleted': { color: 'var(--destructive)' },
    // Selectors, strings, chars
    'selector': { color: 'var(--success)' },
    'attr-name': { color: 'var(--chart-1)' },
    'string': { color: 'var(--success)' },
    'char': { color: 'var(--success)' },
    'builtin': { color: 'var(--chart-3)' },
    'inserted': { color: 'var(--success)' },
    // Operators, entities, urls
    'operator': { color: 'var(--warning)' },
    'entity': { color: 'var(--foreground)', cursor: 'help' },
    'url': { color: 'var(--primary)' },
    // Keywords, functions, class names
    'atrule': { color: 'var(--chart-4)' },
    'attr-value': { color: 'var(--success)' },
    'keyword': { color: 'var(--chart-4)' },
    'function': { color: 'var(--chart-1)' },
    'class-name': { color: 'var(--chart-1)' },
    // Regex, important, variable
    'regex': { color: 'var(--warning)' },
    'important': { color: 'var(--warning)', fontWeight: 'bold' },
    'variable': { color: 'var(--chart-2)' },
    // Bold, italic
    'bold': { fontWeight: 'bold' },
    'italic': { fontStyle: 'italic' },
};

// Map sideways emoticons to Unicode emojis
const emoticonMap = {
    ':)': '😊',
    ':-)': '😊',
    ':(': '😢',
    ':-(': '😢',
    ':D': '😃',
    ':-D': '😃',
    '<3': '❤️',
    ':P': '😛',
    ':-P': '😛',
    ';)': '😉',
    ';-)': '😉',
    ':O': '😮',
    ':-O': '😮',
    ':|': '😐',
    ':-|': '😐',
    ':/': '😕',
    ':-/': '😕',
    ':\\': '😕',
    ':-\\': '😕',
    ':*': '😘',
    ':-*': '😘',
    '>:(': '😠',
    '>:-(': '😠',
    ':\'(': '😭',
    ':\'-(': '😭',
    'XD': '😆',
    'xD': '😆',
    'xP': '😝',
    'XP': '😝',
};

// Convert emoticons to emojis
const convertEmoticons = (text) => {
    if (!text || typeof text !== 'string') return text;

    let converted = text;
    const patterns = Object.keys(emoticonMap).sort((a, b) => b.length - a.length);

    for (const pattern of patterns) {
        const regex = new RegExp(
            `(?<![\\w])${pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![\\w])`,
            'g'
        );
        converted = converted.replace(regex, emoticonMap[pattern]);
    }

    return converted;
};

// Detect if a line looks like ASCII art or a diagram that needs space preservation
const isAsciiArtLine = (line) => {
    // Check for vertical line characters (common in diagrams) - any line with | is likely ASCII art
    if (/\|/.test(line)) return true;
    
    // Check for horizontal lines made of repeated characters (=== or --- with 4+ chars)
    if (/[=]{4,}|[-]{4,}/.test(line)) return true;
    
    // Check for box-drawing patterns like [Text] with significant spacing between brackets
    if (/\[.+\].*  .*\[.+\]/.test(line)) return true;
    
    // Check for tree/hierarchy drawing characters
    if (/[├└┌┐│─┬┴┼]/.test(line)) return true;
    
    // Check for lines that are mostly whitespace with bracketed text (diagram headers/nodes)
    if (/^\s*\[.+\]\s*$/.test(line) && /  /.test(line)) return true;
    
    return false;
};

// Preserve consecutive spaces in ASCII art lines by replacing with non-breaking spaces
// This prevents HTML from collapsing them during rendering
const preserveAsciiArtSpaces = (text) => {
    if (!text || typeof text !== 'string') return text;
    
    const lines = text.split('\n');
    const processedLines = lines.map(line => {
        if (isAsciiArtLine(line)) {
            // Check for trailing "  " used for markdown line breaks - preserve those as regular spaces
            const hasTrailingBreak = line.endsWith('  ');
            let processedLine = line;
            
            if (hasTrailingBreak) {
                // Process everything except trailing spaces
                processedLine = line.slice(0, -2);
                processedLine = processedLine.replace(/ {2,}/g, (match) => '\u00A0'.repeat(match.length));
                processedLine += '  '; // Add back regular spaces for markdown line break
            } else {
                processedLine = line.replace(/ {2,}/g, (match) => '\u00A0'.repeat(match.length));
            }
            return processedLine;
        }
        return line;
    });
    return processedLines.join('\n');
};

// Custom components for react-markdown
const markdownComponents = {
    // Headings
    h1: ({ children }) => (
        <span className="text-xl font-bold block mt-4 mb-2 first:mt-0">{children}</span>
    ),
    h2: ({ children }) => (
        <span className="text-lg font-bold block mt-4 mb-2 first:mt-0">{children}</span>
    ),
    h3: ({ children }) => (
        <span className="text-base font-semibold block mt-3 mb-2 first:mt-0">{children}</span>
    ),
    h4: ({ children }) => (
        <span className="text-sm font-semibold block mt-3 mb-1.5 first:mt-0">{children}</span>
    ),
    h5: ({ children }) => (
        <span className="text-sm font-medium block mt-2.5 mb-1.5 first:mt-0">{children}</span>
    ),
    h6: ({ children }) => (
        <span className="text-xs font-medium block text-muted-foreground mt-2 mb-1 first:mt-0">{children}</span>
    ),
    // Render links with proper styling
    a: ({ href, children }) => (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline break-words"
            style={{ overflowWrap: 'anywhere' }}
        >
            {children}
        </a>
    ),
    // Code blocks and inline code
    code: ({ node, inline, className, children, ...props }) => {
        const match = /language-(\w+)/.exec(className || '');
        const language = match ? match[1] : '';
        const codeString = String(children).replace(/\n$/, '');

        // Check if it's a code block (has language or is multiline)
        const isCodeBlock = !inline && (match || codeString.includes('\n'));

        if (isCodeBlock) {
            return (
                <div className="my-3 first:mt-0">
                    <SyntaxHighlighter
                        style={customCodeTheme}
                        language={language || 'text'}
                        PreTag="div"
                        customStyle={{
                            maxHeight: '300px',
                        }}
                        codeTagProps={{
                            style: {
                                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                            }
                        }}
                        {...props}
                    >
                        {codeString}
                    </SyntaxHighlighter>
                </div>
            );
        }

        // Inline code - uses primary/chart colors from theme
        return (
            <code
                className="px-1.5 py-0.5 bg-muted rounded text-[12px] font-mono border border-border"
                style={{ color: 'var(--chart-3)' }}
                {...props}
            >
                {children}
            </code>
        );
    },
    // Pre tag - just render children (SyntaxHighlighter handles its own wrapper)
    pre: ({ children }) => <>{children}</>,
    // Bold text
    strong: ({ children }) => (
        <strong className="font-semibold">{children}</strong>
    ),
    // Italic text
    em: ({ children }) => (
        <em className="italic">{children}</em>
    ),
    // Strikethrough
    del: ({ children }) => (
        <del className="line-through opacity-70">{children}</del>
    ),
    // Ordered list - use list-outside for proper indentation
    ol: ({ children }) => (
        <ol className="list-decimal list-outside my-2 space-y-0.5 first:mt-0 ml-6 [&_ol]:ml-4 [&_ul]:ml-4 [&_ol_li]:pl-1 [&_ul_li]:pl-1">
            {children}
        </ol>
    ),
    // Unordered list - use list-outside for proper indentation
    ul: ({ children }) => (
        <ul className="list-disc list-outside my-2 space-y-0.5 first:mt-0 ml-6 [&_ol]:ml-4 [&_ul]:ml-4 [&_ol_li]:pl-1 [&_ul_li]:pl-1">
            {children}
        </ul>
    ),
    // List item - with special handling for task list items and nested lists
    li: ({ children, className }) => {
        const isTaskItem = className?.includes('task-list-item');
        return (
            <li className={`text-[13px] ${isTaskItem ? 'list-none gap-1.5' : ''}`}>
                {children}
            </li>
        );
    },
    // Task list checkbox
    input: ({ type, checked, disabled }) => {
        if (type === 'checkbox') {
            return (
                <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    readOnly
                    className="mt-0.5 h-3.5 w-3.5 rounded border-muted-foreground/50 text-primary focus:ring-primary cursor-default"
                />
            );
        }
        return null;
    },
    // Images
    img: ({ src, alt, title }) => (
        <img
            src={src}
            alt={alt || 'Image'}
            title={title}
            className="max-w-full h-auto rounded-md my-2 max-h-[200px] object-contain cursor-pointer hover:opacity-90 transition-opacity first:mt-0"
            onClick={(e) => {
                e.stopPropagation();
                // Open image in new tab on click
                window.open(src, '_blank');
            }}
            onError={(e) => {
                e.target.style.display = 'none';
            }}
        />
    ),
    // Paragraphs with proper spacing
    p: ({ children }) => (
        <span className="block my-2 first:mt-0 last:mb-0">{children}</span>
    ),
    // Horizontal rule
    hr: () => (
        <hr className="my-3 border-border first:mt-0" />
    ),
    // Blockquote
    blockquote: ({ children }) => (
        <blockquote className="border-l-4 border-primary/50 pl-4 my-2 italic text-muted-foreground first:mt-0">
            {children}
        </blockquote>
    ),
    // Tables using shadcn components
    table: ({ children }) => (
        <div className="my-3 rounded-md border overflow-hidden first:mt-0">
            <Table>{children}</Table>
        </div>
    ),
    thead: ({ children }) => (
        <TableHeader className="bg-muted/50">{children}</TableHeader>
    ),
    tbody: ({ children }) => (
        <TableBody>{children}</TableBody>
    ),
    tr: ({ children }) => (
        <TableRow>{children}</TableRow>
    ),
    th: ({ children }) => (
        <TableHead className="text-xs font-semibold">{children}</TableHead>
    ),
    td: ({ children }) => (
        <TableCell className="text-[13px]">{children}</TableCell>
    ),
};

const MarkdownText = ({ children, className = '' }) => {
    const textContent = typeof children === 'string' ? children : String(children || '');
    const textWithEmojis = convertEmoticons(textContent);
    
    // Check if text contains lists (markdown list patterns - including indented ones)
    const hasLists = /(^|\n)(\s*)([-*+]|\d+\.)\s/m.test(textWithEmojis);
    
    let processedText = textWithEmojis;
    
    if (hasLists) {
        // If text contains lists, preserve list structure exactly
        // Normalize indentation for nested lists (markdown requires 2 spaces per level)
        const lines = processedText.split('\n');
        const processedLines = [];
        let inList = false;
        
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            // Match list items with any indentation (including single space)
            const listMatch = line.match(/^(\s*)([-*+]|\d+\.)\s(.*)$/);
            const isEmpty = line.trim() === '';
            
            if (listMatch) {
                inList = true;
                const [, indent, marker, content] = listMatch;
                // Normalize indentation: markdown REQUIRES exactly 2 spaces per nesting level
                // Level 1: 0 spaces, Level 2: 2 spaces, Level 3: 4 spaces, Level 4: 6 spaces, etc.
                let normalizedIndent = '';
                if (indent.length > 0) {
                    // Round to nearest multiple of 2 (markdown standard)
                    // 1 space -> 2 spaces, 3 spaces -> 4 spaces, 5 spaces -> 6 spaces, etc.
                    const normalizedLength = Math.round(indent.length / 2) * 2;
                    normalizedIndent = '  '.repeat(normalizedLength / 2);
                }
                // Ensure we have proper 2-space indentation for nested lists
                processedLines.push(`${normalizedIndent}${marker} ${content}`);
            } else if (isEmpty && inList) {
                // Empty line after list - end of list block
                inList = false;
                processedLines.push('');
            } else if (isEmpty) {
                // Empty line outside list
                processedLines.push('');
            } else if (inList && /^\s/.test(line)) {
                // Continuation of list item (indented content) - preserve as is
                processedLines.push(line);
            } else if (inList) {
                // Non-indented line in list context - might be end of list
                inList = false;
                processedLines.push(line);
            } else {
                // Regular text - convert single newlines to markdown breaks
                processedLines.push(line + '  ');
            }
        }
        
        processedText = processedLines.join('\n');
        // Clean up trailing spaces from non-list lines
        processedText = processedText.replace(/([^\n])  \n/g, '$1\n');
    } else {
        // No lists - apply normal newline conversion
        const PARAGRAPH_PLACEHOLDER = '___PARAGRAPH_BREAK___';
        processedText = processedText.replace(/\n\n+/g, PARAGRAPH_PLACEHOLDER);
        processedText = processedText.replace(/\n/g, '  \n');
        processedText = processedText.replace(new RegExp(PARAGRAPH_PLACEHOLDER, 'g'), '\n\n');
    }

    // Preserve spaces in ASCII art lines for display (after list detection is done)
    processedText = preserveAsciiArtSpaces(processedText);

    return (
        <div
            className={className}
            style={{ wordBreak: 'break-word', overflowWrap: 'anywhere', display: 'block' }}
        >
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={markdownComponents}
            >
                {processedText}
            </ReactMarkdown>
        </div>
    );
};

export default MarkdownText;
